import * as React from "react"
import { cn } from "../../lib/cn"
import { SubsectionTitle, MetaText } from "./typography"

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
    title: string
    description: string
    icon?: React.ElementType
    action?: React.ReactNode
    actionPosition?: 'top' | 'bottom'
    /** Heading level for the title (default 3). Set to 1 or 2 so the empty state's
        title fits the host page's heading outline instead of always rendering as an
        h3 (which skips a level under an h1 page title). Styling is unchanged. */
    headingLevel?: 1 | 2 | 3 | 4 | 5 | 6
}

const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
    ({ className, title, description, icon: Icon, action, actionPosition = 'bottom', headingLevel = 3, ...props }, ref) => {
        return (
            <div
                ref={ref}
                className={cn(
                    // UI-127 (spec RULED E4) — was `animate-in fade-in-50`, which needs an
                    // animation plugin this project does not install, so this panel has never
                    // actually faded on any of its 9 sites. `animate-fade-in` is defined in
                    // index.css INSIDE a `prefers-reduced-motion: no-preference` guard, so it
                    // animates for people who want motion and is absent for those who do not.
                    // ⚠️ Do NOT swap this back to an `animate-in`/`fade-in-*` class: those are
                    // library utilities, and the library is not here. Check tailwind.config.js.
                    "flex flex-col items-center text-center gap-3 py-12 max-w-md mx-auto w-full animate-fade-in",
                    className
                )}
                {...props}
            >
                {Icon && (
                    <div className="flex bg-surface-base items-center justify-center w-12 h-12 rounded-xl mb-1 border border-border-default">
                        <Icon strokeWidth={1.5} className="h-6 w-6 text-text-muted" />
                    </div>
                )}

                {action && actionPosition === 'top' && (
                    <div className="mb-4 flex items-center gap-2 justify-center w-full">{action}</div>
                )}

                <div className="space-y-1 w-full">
                    <SubsectionTitle as={`h${headingLevel}` as React.ElementType} className="text-center">{title}</SubsectionTitle>
                    <MetaText className="max-w-[60ch] mx-auto text-text-muted block">
                        {description}
                    </MetaText>
                </div>

                {action && actionPosition === 'bottom' && (
                    <div className="mt-4 flex items-center gap-2 justify-center w-full">{action}</div>
                )}
            </div>
        )
    }
)
EmptyState.displayName = "EmptyState"

export { EmptyState }
